/**
 * Abandoned in Queue widget — metrics only (no list; use Abandoned Calls table widget for records).
 * Metric tiles expand to fill the widget.
 */
import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { buildCallHistoryParams, type WidgetFilterConfig } from '../../lib/widgetScope';

export interface AbandonedCallsStats {
  abandoned_count: number;
  answered_count: number;
  total_calls: number;
  percent_abandoned: number;
}

interface WidgetAbandonedInQueueProps {
  config?: WidgetFilterConfig;
}

export default function WidgetAbandonedInQueue({ config }: WidgetAbandonedInQueueProps) {
  const { getToken } = useAuth();
  const [stats, setStats] = useState<AbandonedCallsStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const base = typeof window !== 'undefined' ? window.location.origin : '';
      const params = buildCallHistoryParams(config);
      params.set('limit', '20');
      try {
        const res = await fetch(`${base}/api/abandoned-calls?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.status === 403) {
          setError('Supervisor or admin access required');
          setStats(null);
          return;
        }
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data.error || `HTTP ${res.status}`);
          return;
        }
        const data = await res.json();
        setStats(data.stats ?? null);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken, config?.scope, config?.campaign_id, config?.ipscape_agent_id, config?.date_range]);

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: '0.5rem 0.75rem',
        containerType: 'size',
        containerName: 'abandoned-widget',
      }}
    >
      {loading && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 'clamp(0.75rem, 2cqi, 1rem)' }}>
          Loading…
        </div>
      )}
      {error && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--error)', fontSize: 'clamp(0.75rem, 2cqi, 1rem)', textAlign: 'center' }}>
          {error}
        </div>
      )}
      {!loading && !error && stats !== null && (
        <div
          style={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 'clamp(0.5rem, 2cqi, 1rem)',
            minHeight: 0,
            alignContent: 'stretch',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              padding: 'clamp(0.5rem, 2cqi, 1.5rem)',
              background: 'var(--bg)',
              borderRadius: 8,
              textAlign: 'center',
            }}
          >
            <div style={{ color: 'var(--text-muted)', fontSize: 'clamp(0.7rem, 2cqi, 0.95rem)', marginBottom: '0.25rem' }}>Abandoned</div>
            <div style={{ fontSize: 'clamp(1.25rem, 6cqi, 3rem)', fontWeight: 700 }}>{stats.abandoned_count}</div>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              padding: 'clamp(0.5rem, 2cqi, 1.5rem)',
              background: 'var(--bg)',
              borderRadius: 8,
              textAlign: 'center',
            }}
          >
            <div style={{ color: 'var(--text-muted)', fontSize: 'clamp(0.7rem, 2cqi, 0.95rem)', marginBottom: '0.25rem' }}>% Abandoned</div>
            <div style={{ fontSize: 'clamp(1.25rem, 6cqi, 3rem)', fontWeight: 700 }}>{stats.percent_abandoned}%</div>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              padding: 'clamp(0.5rem, 2cqi, 1.5rem)',
              background: 'var(--bg)',
              borderRadius: 8,
              textAlign: 'center',
            }}
          >
            <div style={{ color: 'var(--text-muted)', fontSize: 'clamp(0.7rem, 2cqi, 0.95rem)', marginBottom: '0.25rem' }}>Inbound answered</div>
            <div style={{ fontSize: 'clamp(1.25rem, 6cqi, 3rem)', fontWeight: 700 }}>{stats.answered_count}</div>
          </div>
        </div>
      )}
    </div>
  );
}
